import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

import useChatSocket from "@/components/chat/useChatSocket";
import { LiveshowNotificationModal } from "@/components/home/modals/LiveshowNotificationModal";
import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import { LiveShowsQueries } from "@/data/liveShows/liveShows.queries";
import { useLiveshowModalStore, useLiveshowsStore } from "@/providers/liveshows/liveshows.store";

export const GlobalLiveshowModal = () => {
  const router = useRouter();
  const { isLoggedIn, user } = AuthContext.useAuth();
  const { setHasActiveLiveshow } = useLiveshowsStore();
  const { showLiveshowModal, setShowLiveshowModal, setShouldAutoplay } = useLiveshowModalStore();
  const { chatSocket } = useChatSocket();
  const queryClient = useQueryClient();
  const { refetch: refetchLiveshows } = LiveShowsQueries.useGetCurrent({
    enabled: false,
  });

  const isOnboardingCompleted = user?.onboardingStep === CommonModels.OnboardingStepEnum.completed;

  const handleFetchLiveshows = useCallback(async () => {
    const liveshows = await refetchLiveshows();
    if (liveshows.data?.length && liveshows.data.length > 0) {
      setHasActiveLiveshow(true);
    } else {
      setHasActiveLiveshow(false);
    }
  }, [setHasActiveLiveshow, refetchLiveshows]);

  const isOnLiveshowPage = useMemo(() => {
    return router.pathname === RouteConfig.Liveshow.slice(0, -1);
  }, [router.pathname]);

  const subscribeToLiveshowEvents = useCallback(() => {
    if (!chatSocket) {
      return;
    }

    chatSocket.on("live-show", (data: { action: string; type: string; liveShow: CommonModels.LiveShowResponseDto }) => {
      if (
        data.action === "started" ||
        (data.liveShow.status === CommonModels.LiveShowStatusEnum.LIVE && data.action === "updated")
      ) {
        setHasActiveLiveshow(true);
        if (!isOnLiveshowPage) {
          setShowLiveshowModal(true);
        }
      }

      if (data.action === "updated" || data.action === "started" || data.action === "created") {
        queryClient.invalidateQueries({ queryKey: LiveShowsQueries.keys.getUpcoming(10) });
        handleFetchLiveshows();
        return;
      }

      if (data.action === "ended") {
        queryClient.invalidateQueries({ queryKey: LiveShowsQueries.keys.getUpcoming(10) });
        queryClient.invalidateQueries({ queryKey: LiveShowsQueries.keys.getPast(10) });
        handleFetchLiveshows();
      }
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSocket, setHasActiveLiveshow, setShowLiveshowModal, isOnLiveshowPage]);

  const unsubscribeFromLiveshowEvents = useCallback(() => {
    if (chatSocket) {
      chatSocket.off("live-show");
    }
  }, [chatSocket]);

  const handleWatchNow = useCallback(() => {
    setShowLiveshowModal(false);
    setShouldAutoplay(true);
    // Navigate to liveshow page
    window.location.href = "/liveshow";
  }, [setShowLiveshowModal, setShouldAutoplay]);

  useEffect(() => {
    if (!isLoggedIn || !isOnboardingCompleted) {
      return undefined;
    }
    subscribeToLiveshowEvents();
    return () => {
      unsubscribeFromLiveshowEvents();
    };
  }, [subscribeToLiveshowEvents, unsubscribeFromLiveshowEvents, isLoggedIn, isOnboardingCompleted]);

  // Fetch current liveshows on mount to initialize the store
  useEffect(() => {
    if (!isLoggedIn || !isOnboardingCompleted) {
      return;
    }
    handleFetchLiveshows();
  }, [isLoggedIn, isOnboardingCompleted, handleFetchLiveshows]);

  if (!isLoggedIn || !isOnboardingCompleted || isOnLiveshowPage) {
    return null;
  }

  return (
    <LiveshowNotificationModal
      isOpen={showLiveshowModal}
      onClose={() => setShowLiveshowModal(false)}
      onWatchNow={handleWatchNow}
    />
  );
};
